import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../../utils/http";

const initialState = {
  isLoading: false,
  storeLocations: { data: [], meta: {} },
  storeDetail: {},
};

export const fetchStoreLocations = createAsyncThunk(
  "storeLocation/fetchAll",
  async ({ page = 1, limit = 12, search = "", sort = "newest" }, thunkAPI) => {
    try {
      const params = new URLSearchParams({ page, limit, sort });
      if (search) params.append("search", search);
      const response = await http.get(`store/fetch-stores?${params.toString()}`);
      // Normalize response — API may return { stores, totalStores } or { data: [...] }
      const raw = response.data;
      const stores =
        raw?.data?.stores ??
        raw?.data?.data ??
        raw?.stores ??
        (Array.isArray(raw?.data) ? raw.data : null) ??
        [];
      const total =
        raw?.data?.pagination?.totalCount ??
        raw?.data?.totalStores ??
        raw?.totalStores ??
        raw?.data?.total ??
        raw?.total ??
        stores.length;
      const totalPages =
        raw?.data?.pagination?.totalPages ??
        (Math.ceil(total / limit) || 1);
      return thunkAPI.fulfillWithValue({ data: stores, meta: { totalPages, total } });
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchStoreById = createAsyncThunk(
  "storeLocation/fetchById",
  async (id, thunkAPI) => {
    try {
      const response = await http.get(`store/fetch-store/${id}`);
      const raw = response.data;
      const store = raw?.data ?? raw;
      return thunkAPI.fulfillWithValue({ data: store });
    } catch (error) {
      // fallback: try fetching from the list already in state
      const state = thunkAPI.getState();
      const found = state?.storeLocationReducer?.storeLocations?.data?.find(
        (s) => s._id === id
      );
      if (found) return thunkAPI.fulfillWithValue({ data: found });
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const createStoreLocation = createAsyncThunk(
  "storeLocation/create",
  async (payload, thunkAPI) => {
    try {
      const response = await http.post(`store/create-store`, payload);
      return thunkAPI.fulfillWithValue(response.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteStoreLocation = createAsyncThunk(
  "storeLocation/delete",
  async (id, thunkAPI) => {
    try {
      const response = await http.delete(`store/${id}`);
      return thunkAPI.fulfillWithValue(response.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateStoreLocation = createAsyncThunk(
  "storeLocation/update",
  async ({ id, body }, thunkAPI) => {
    try {
      const response = await http.put(`store/update/${id}`, body);
      return thunkAPI.fulfillWithValue(response.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const storeLocationSlice = createSlice({
  name: "storeLocation",
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(fetchStoreLocations.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchStoreLocations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.storeLocations = action.payload;
      })
      .addCase(fetchStoreLocations.rejected, (state) => {
        state.isLoading = false;
        state.storeLocations = { data: [], meta: {} };
      })
      .addCase(fetchStoreById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchStoreById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.storeDetail = action.payload;
      })
      .addCase(fetchStoreById.rejected, (state) => {
        state.isLoading = false;
        state.storeDetail = {};
      })
      .addCase(createStoreLocation.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createStoreLocation.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(createStoreLocation.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(updateStoreLocation.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateStoreLocation.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateStoreLocation.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteStoreLocation.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteStoreLocation.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteStoreLocation.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export default storeLocationSlice.reducer;
